import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import type { AuthError, User } from '@supabase/supabase-js'
import { FIRST_RUN_KEY, NEW_ACCOUNT } from '../lib/firstRun'

/** A failed auth call: the message to show, and GoTrue's code to branch on. */
export interface AuthFailure {
  message: string
  /** e.g. 'email_not_confirmed', 'otp_expired', 'over_email_send_rate_limit' */
  code?: string
}

function failure(error: AuthError | null): AuthFailure | null {
  return error ? { message: error.message, code: error.code } : null
}

export type AuthActions = Omit<ReturnType<typeof useAuth>, 'user' | 'loading'>

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  // Set while resetPassword runs; see there.
  const recovering = useRef(false)

  useEffect(() => {
    // getSession() reads persisted storage without a network call, so a visitor
    // with no stored session gets the landing view painted immediately instead of
    // sitting on "Loading…" through a round-trip. "/" is the marketing page as
    // well as the app, and that wait is the first thing a new visitor would see.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        setUser(null)
        setLoading(false)
        return
      }

      // There is a stored session — refreshSession() re-signs the access token with
      // the current JWT signing key. If it fails (e.g. after a JWT key rotation that
      // invalidated refresh tokens), sign out so the user gets a fresh HS256 JWT on
      // next login rather than being stuck with a 400-producing ECC JWT indefinitely.
      supabase.auth.refreshSession().then(({ data, error }) => {
        if (!error && data.session) {
          setUser(data.session.user)
          setLoading(false)
        } else {
          supabase.auth.signOut().then(() => {
            setUser(null)
            setLoading(false)
          })
        }
      })
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      if (recovering.current) return
      setUser(session?.user ?? null)
    })

    return () => subscription.unsubscribe()
  }, [])

  async function signIn(email: string, password: string): Promise<AuthFailure | null> {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return failure(error)
  }

  // Every auth mail carries an 8-digit code typed back into the app rather than
  // a link: on iOS a link opens in Safari, whose storage isn't the installed
  // PWA's, so the session would land in the wrong app. Hence no emailRedirectTo.
  // A new account is marked for the first run (lib/firstRun.ts); older ones carry no mark.
  async function signUp(email: string, password: string): Promise<AuthFailure | null> {
    const { error } = await supabase.auth.signUp({
      email, password, options: { data: { [FIRST_RUN_KEY]: NEW_ACCOUNT } },
    })
    return failure(error)
  }

  /** The signup code; a right one signs the user straight in. */
  async function verifySignup(email: string, code: string): Promise<AuthFailure | null> {
    const { error } = await supabase.auth.verifyOtp({ email, token: code, type: 'signup' })
    return failure(error)
  }

  async function resendSignup(email: string): Promise<AuthFailure | null> {
    const { error } = await supabase.auth.resend({ type: 'signup', email })
    return failure(error)
  }

  /** Mails a recovery code. Answers the same whether or not the account exists. */
  async function sendPasswordReset(email: string): Promise<AuthFailure | null> {
    const { error } = await supabase.auth.resetPasswordForEmail(email)
    return failure(error)
  }

  /**
   * The recovery code and the new password together. A right code signs the
   * user in, which would swap the login page for the list before the password
   * is changed — so the session isn't shown until updateUser has run, and a
   * failed update signs back out rather than leaving the old password in place
   * behind a signed-in screen.
   */
  async function resetPassword(email: string, code: string, password: string): Promise<AuthFailure | null> {
    recovering.current = true
    try {
      const verified = await supabase.auth.verifyOtp({ email, token: code, type: 'recovery' })
      if (verified.error) return failure(verified.error)
      const updated = await supabase.auth.updateUser({ password })
      if (updated.error) {
        await supabase.auth.signOut()
        return failure(updated.error)
      }
      setUser(updated.data.user)
      return null
    } finally {
      recovering.current = false
    }
  }

  async function signOut() {
    await supabase.auth.signOut()
  }

  return { user, loading, signIn, signUp, verifySignup, resendSignup, sendPasswordReset, resetPassword, signOut }
}
